import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Search, ShoppingCart, Filter, Grid, List, Package, Star } from 'lucide-react'
import { Product, Category } from '../types'
import { api } from '../lib/api'
import { useCart } from '../contexts/CartContext'
import { useStore } from '../contexts/StoreContext'
import { useStoreDetail } from '../contexts/StoreDetailContext'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import ProductImage from '../components/ProductImage'
import { calculateStoreIsOpen, getNextOpeningTime } from '../lib/storeUtils'
import { getCache, getCacheStale, setCache } from '../lib/cache'
import { showOfflineToast } from '../lib/toastManager'

const Catalog = () => {
  const { storeId } = useParams<{ storeId: string }>()
  const navigate = useNavigate()
  const { addItem } = useCart()
  const { stores } = useStore()
  const { getStore, getStoreSync } = useStoreDetail() // Usar StoreDetailContext
  const { isAuthenticated } = useAuth()
  
  const [store, setStore] = useState<any>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showFilters, setShowFilters] = useState(false)
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'price-asc' | 'price-desc' | 'featured'>('featured')
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Refs para evitar múltiples llamadas simultáneas
  const loadingRef = useRef(false)
  const loadedStoreIdRef = useRef<string | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const loadingProductsRef = useRef(false)
  const loadingCategoriesRef = useRef(false)

  // Buscar store en el contexto primero, si no está, cargarlo desde la API
  const currentStore = store || stores.find(s => s.id === storeId)

  useEffect(() => {
    // Evitar múltiples llamadas simultáneas para el mismo storeId
    if (!storeId) return
    
    // Si ya se está cargando este storeId, no volver a cargar
    if (loadingRef.current && loadedStoreIdRef.current === storeId) {
      return
    }

    // Si ya se cargó este storeId y tenemos datos, no volver a cargar
    if (loadedStoreIdRef.current === storeId && products.length > 0) {
      return
    }

    loadingRef.current = true
    loadedStoreIdRef.current = storeId

    const loadData = async () => {
      try {
        await Promise.all([
          loadStore(),
          loadProducts(),
          loadCategories()
        ])
      } catch (error) {
        console.error('Error loading catalog data:', error)
      } finally {
        loadingRef.current = false
      }
    }

    loadData()

    // Cleanup: reset refs cuando cambia storeId
    return () => {
      if (loadedStoreIdRef.current !== storeId) {
        loadedStoreIdRef.current = null
        loadingRef.current = false
      }
    }
  }, [storeId])

  const loadStore = async () => {
    if (!storeId) return
    
    // Si ya está en el contexto, no cargar
    if (stores.find(s => s.id === storeId)) {
      return
    }
    
    // Si ya se cargó este store, no volver a cargar
    if (store && store.id === storeId) {
      return
    }
    
    // Intentar cargar desde cache sincrónicamente primero (más rápido)
    const cachedStore = getStoreSync(storeId)
    if (cachedStore) {
      setStore(cachedStore)
      return
    }
    
    try {
      // Usar StoreDetailContext para cargar el store (elimina query duplicada)
      const loadedStore = await getStore(storeId)
      if (loadedStore) {
        setStore(loadedStore)
      }
    } catch (error) {
      console.error('Error loading store:', error)
    }
  }

  useEffect(() => {
    filterAndSortProducts()
  }, [products, searchTerm, selectedCategory, minPrice, maxPrice, sortBy])

  // Detectar scroll para ocultar/mostrar header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Si estamos en la parte superior, siempre mostrar el header
      if (currentScrollY < 50) {
        setIsHeaderVisible(true)
        setLastScrollY(currentScrollY)
        return
      }

      // Si el scroll es hacia arriba (scrollY disminuye), ocultar header
      // Si el scroll es hacia abajo (scrollY aumenta), mostrar header
      if (currentScrollY < lastScrollY && currentScrollY > 100) {
        // Scroll hacia arriba - ocultar header
        setIsHeaderVisible(false)
      } else if (currentScrollY > lastScrollY) {
        // Scroll hacia abajo - mostrar header
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  const loadProducts = async () => {
    if (!storeId) return
    
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingProductsRef.current) return
    loadingProductsRef.current = true
    
    const cacheKey = `catalog_products_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedProducts = getCacheStale<Product[]>(cacheKey)
    if (cachedProducts && cachedProducts.length > 0) {
      setProducts(cachedProducts)
      setLoading(false)
      loadingProductsRef.current = false
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }
    
    try {
      setLoading(true)
      const response = await api.get(`/products?storeId=${storeId}&isAvailable=true&limit=100`)
      if (response.data.success) {
        // Normalizar los productos para asegurar que price sea un número y mapear image_url
        const normalizedProducts = (response.data.data || []).map((product: any) => ({
          ...product,
          price: Number(product.price) || 0,
          imageUrl: product.image_url || product.imageUrl, // Mapear image_url a imageUrl
          stockQuantity: Number(product.stock_quantity) || 0,
          isAvailable: product.is_available !== false,
          isFeatured: product.is_featured || false,
          variants: product.variants || [],
          has_variants: product.has_variants || (product.variants && product.variants.length > 0)
        }))
        setProducts(normalizedProducts)
        
        // Guardar en cache (30 minutos de TTL)
        setCache(cacheKey, normalizedProducts, 30 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading products:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedProducts) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // Solo mostrar error si no es 429 (rate limit)
      if (error.response?.status !== 429) {
        toast.error('Error al cargar productos')
      }
      
      // Solo borrar productos si no hay datos en cache
      if (!cachedProducts || cachedProducts.length === 0) {
        setProducts([])
      }
    } finally {
      setLoading(false)
      loadingProductsRef.current = false
    }
  }

  const loadCategories = async () => {
    if (!storeId) return
    
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingCategoriesRef.current) return
    loadingCategoriesRef.current = true
    
    const cacheKey = `catalog_categories_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedCategories = getCacheStale<Category[]>(cacheKey)
    if (cachedCategories && cachedCategories.length > 0) {
      setCategories(cachedCategories)
      loadingCategoriesRef.current = false
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }
    
    try {
      const response = await api.getStoreCategories(storeId)
      if (response.success) {
        const categoriesData = response.data || []
        setCategories(categoriesData)
        
        // Guardar en cache (2 horas de TTL, las categorías cambian poco)
        setCache(cacheKey, categoriesData, 2 * 60 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading categories:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedCategories) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // Solo borrar categorías si no hay datos en cache
      if (!cachedCategories || cachedCategories.length === 0) {
        setCategories([])
      }
    } finally {
      loadingCategoriesRef.current = false
    }
  }

  const filterAndSortProducts = () => {
    let filtered = [...products]

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Filtrar por categoría
    if (selectedCategory) {
      filtered = filtered.filter(p => p.categoryId === selectedCategory)
    }

    // Filtrar por precio
    if (minPrice) {
      const min = parseFloat(minPrice)
      if (!isNaN(min)) {
        filtered = filtered.filter(p => p.price >= min)
      }
    }

    if (maxPrice) {
      const max = parseFloat(maxPrice)
      if (!isNaN(max)) {
        filtered = filtered.filter(p => p.price <= max)
      }
    }

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'price-asc':
          return a.price - b.price
        case 'price-desc':
          return b.price - a.price
        case 'featured':
        default:
          if (a.isFeatured && !b.isFeatured) return -1
          if (!a.isFeatured && b.isFeatured) return 1
          return 0
      }
    })

    setFilteredProducts(filtered)
  }

  const handleAddToCart = (product: Product, qty: number = 1) => {
    // Verificar autenticación antes de agregar al carrito
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para agregar productos al carrito')
      navigate('/login', { state: { from: `/stores/${storeId}/catalog` } })
      return
    }

    if (!currentStore) {
      toast.error('Negocio no encontrado')
      return
    }

    // Si el producto tiene variantes, navegar a la página de detalle
    if (product.has_variants && product.variants && product.variants.length > 0) {
      handleViewProduct(product)
      return
    }

    // Producto sin variantes - agregar directamente
    if (!product.isAvailable || product.stockQuantity === 0) {
      toast.error('Producto no disponible')
      return
    }

    if (qty > product.stockQuantity) {
      toast.error(`Solo hay ${product.stockQuantity} unidades disponibles`)
      return
    }

    addItem(product, currentStore, qty)
    toast.success(`${qty} ${product.name} agregado${qty > 1 ? 's' : ''} al carrito`)
  }

  const handleViewProduct = (product: Product) => {
    // Navegar a la página de detalle del producto
    navigate(`/stores/${storeId}/product?id=${product.id}`)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedCategory('')
    setMinPrice('')
    setMaxPrice('')
    setSortBy('featured')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando productos...</p>
        </div>
      </div>
    )
  }

  if (!currentStore) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Negocio no encontrado</h2>
          <p className="mt-2 text-gray-600">El negocio que buscas no existe o no está disponible</p>
          <Link
            to="/stores"
            className="mt-4 inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Ver Negocios
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div ref={scrollContainerRef} className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24 md:pb-20">
      {/* Header - iPhone Style */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 sticky top-0 z-40 shadow-sm transition-transform duration-300 ease-in-out">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          {/* Store Name and Cart - Se oculta al hacer scroll hacia arriba */}
          <div 
            className={`flex items-center justify-between mb-4 sm:mb-5 transition-all duration-300 ease-in-out ${
              isHeaderVisible 
                ? 'opacity-100 max-h-20 translate-y-0' 
                : 'opacity-0 max-h-0 -translate-y-full overflow-hidden mb-0'
            }`}
          >
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate">{currentStore.name}</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Catálogo de productos</p>
            </div>
            <Link
              to={`/stores/${storeId}/cart`}
              className="relative p-2.5 sm:p-3 rounded-full bg-gray-100 hover:bg-gray-200 active:scale-95 transition-all duration-200 ml-3 flex-shrink-0"
            >
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </Link>
          </div>

          {/* Search Bar - iPhone Style */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 sm:py-4 bg-gray-50 border-0 rounded-2xl text-sm sm:text-base focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all duration-200 placeholder:text-gray-400"
            />
          </div>

          {/* Filters and View Toggle - iPhone Style */}
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 active:scale-95 ${
                  showFilters 
                    ? 'bg-primary-600 text-white shadow-md' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filtros</span>
              </button>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              >
                <option value="featured">Destacados</option>
                <option value="name">Nombre A-Z</option>
                <option value="price-asc">Precio: Menor</option>
                <option value="price-desc">Precio: Mayor</option>
              </select>
            </div>
            <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-xl">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 sm:p-2.5 rounded-lg transition-all duration-200 active:scale-95 ${
                  viewMode === 'grid' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Grid className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 sm:p-2.5 rounded-lg transition-all duration-200 active:scale-95 ${
                  viewMode === 'list' 
                    ? 'bg-white text-primary-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <List className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Filters Panel - iPhone Style */}
          {showFilters && (
            <div className="mt-4 p-4 sm:p-5 bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-sm animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Filtros</h3>
                <button
                  onClick={clearFilters}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 active:scale-95 transition-all"
                >
                  Limpiar
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Categoría</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all"
                  >
                    <option value="">Todas</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Precio Mínimo</label>
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="0"
                    className="w-full px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Precio Máximo</label>
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="9999"
                    className="w-full px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Products - iPhone Style */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {!currentStore.isActive ? (
          <div className="text-center py-16 sm:py-20">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 sm:p-12 max-w-md mx-auto shadow-sm border border-gray-200/50">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-red-100 to-rose-100 rounded-full flex items-center justify-center">
                <Package className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Negocio Cerrado</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                El negocio está cerrado en este momento. Los productos no están disponibles para compra.
              </p>
              {(() => {
                const nextOpening = getNextOpeningTime(currentStore.operatingHours)
                if (nextOpening) {
                  return (
                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                      <p className="text-sm sm:text-base text-blue-800 font-medium">
                        <span className="font-semibold">Próxima apertura:</span> {nextOpening.day} a las {nextOpening.time}
                      </p>
                    </div>
                  )
                }
                return null
              })()}
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate(`/stores/${storeId}`)}
                  className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 active:scale-95 transition-all duration-200 shadow-sm"
                >
                  Ver Información del Negocio
                </button>
                <button
                  onClick={() => navigate('/stores')}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 active:scale-95 transition-all duration-200"
                >
                  Explorar Otros Negocios
                </button>
              </div>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 sm:py-20">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 sm:p-12 max-w-md mx-auto shadow-sm border border-gray-200/50">
              <Package className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No se encontraron productos</h3>
              <p className="text-sm sm:text-base text-gray-500 mb-6">Intenta ajustar los filtros de búsqueda</p>
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 active:scale-95 transition-all duration-200 shadow-sm"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4' 
            : 'space-y-3 sm:space-y-4'
          }>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className={`bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-200/50 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer active:scale-[0.98] ${
                  viewMode === 'list' ? 'flex gap-3 sm:gap-4 p-3 sm:p-4' : 'flex flex-col'
                }`}
                onClick={() => handleViewProduct(product)}
              >
                {viewMode === 'grid' ? (
                  <>
                    <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                      <ProductImage
                        imageKey={product.imageUrl || null}
                        className="w-full h-full object-cover"
                        alt={product.name}
                      />
                      {product.isFeatured && (
                        <div className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-[10px] sm:text-xs px-2 sm:px-2.5 py-1 rounded-full font-bold shadow-sm flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" />
                          <span className="hidden sm:inline">Destacado</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3 sm:p-4 flex flex-col flex-1">
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 mb-1 line-clamp-2 leading-tight">{product.name}</h3>
                      {product.description && (
                        <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3 line-clamp-2 leading-relaxed">{product.description}</p>
                      )}
                      <div className="flex items-center justify-between mb-2 sm:mb-3 mt-auto">
                        <span className="text-base sm:text-lg font-bold text-primary-600">
                          RD${(Number(product.price) || 0).toFixed(2)}
                        </span>
                        <span className="text-[10px] sm:text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{product.unit}</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleAddToCart(product)
                        }}
                        disabled={!product.isAvailable || product.stockQuantity === 0}
                        className={`w-full py-2 sm:py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all duration-200 active:scale-95 ${
                          product.isAvailable && product.stockQuantity > 0
                            ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {product.isAvailable && product.stockQuantity > 0
                          ? 'Agregar'
                          : 'No Disponible'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl overflow-hidden">
                      <ProductImage
                        imageKey={product.imageUrl || null}
                        className="w-full h-full object-cover"
                        alt={product.name}
                      />
                      {product.isFeatured && (
                        <div className="absolute top-1 right-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-[8px] px-1.5 py-0.5 rounded-full font-bold">
                          <Star className="w-2 h-2 fill-current" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2 gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm sm:text-base text-gray-900 mb-1 line-clamp-1">{product.name}</h3>
                          {product.description && (
                            <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 leading-relaxed">{product.description}</p>
                          )}
                        </div>
                        <span className="text-base sm:text-lg font-bold text-primary-600 flex-shrink-0 ml-2">
                          RD${(Number(product.price) || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] sm:text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-full">{product.unit}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (product.has_variants && product.variants && product.variants.length > 0) {
                              handleViewProduct(product)
                            } else {
                              handleAddToCart(product)
                            }
                          }}
                          disabled={!product.isAvailable || (product.stockQuantity === 0 && (!product.has_variants || !product.variants?.some(v => v.is_available && v.stock_quantity > 0)))}
                          className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all duration-200 active:scale-95 ${
                            product.isAvailable && (product.stockQuantity > 0 || (product.has_variants && product.variants?.some(v => v.is_available && v.stock_quantity > 0)))
                              ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {product.has_variants && product.variants && product.variants.length > 0
                            ? 'Ver Opciones'
                            : product.isAvailable && product.stockQuantity > 0
                            ? 'Agregar'
                            : 'No Disponible'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  )
}

export default Catalog
